import React, { useCallback, useEffect, useState } from "react";
import {
  Users,
  Loader2,
  Plus,
  Mail,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Clock,
  X,
  Send,
  Copy,
} from "lucide-react";
import {
  fetchAdminUsers,
  deleteAdminUser,
  fetchAdminInvites,
  createAdminInvite,
  revokeAdminInvite,
} from "@/api/adminUsers";
import { useAdminAuth } from "@/context/AdminAuthContext";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Toast = ({ toast }) =>
  toast ? (
    <div
      data-testid="admin-users-toast"
      className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl shadow-lg font-[Poppins] font-semibold text-sm flex items-center gap-2 ${
        toast.type === "error" ? "bg-[#8b1420] text-white" : "bg-[#173A5E] text-white"
      }`}
    >
      {toast.type === "error" ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
      {toast.message}
    </div>
  ) : null;

const InviteModal = ({ onClose, onSaved, showToast }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      showToast("Vul een e-mailadres in.", "error");
      return;
    }
    setSaving(true);
    try {
      const res = await createAdminInvite({
        email: email.trim().toLowerCase(),
        name: (name || "Beheerder").trim(),
      });
      showToast(`Uitnodiging verstuurd naar ${res.invite.email}`);
      onSaved();
    } catch (err) {
      showToast(err?.response?.data?.detail || "Uitnodigen mislukt", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="invite-modal"
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl my-8"
      >
        <div className="border-b border-[#E4EEF5] px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-xl font-bold text-[#173A5E] font-[Poppins] flex items-center gap-2">
            <Send className="w-5 h-5 text-[#2E9CD6]" />
            Nieuwe beheerder uitnodigen
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="invite-close"
            className="p-2 rounded-lg hover:bg-[#F4F8FB]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-[#5A7594] font-[Inter]">
            De ontvanger krijgt een e-mail met een activatielink (48 uur geldig).
            Ze kiezen zelf hun wachtwoord.
          </p>
          <label className="block">
            <span className="text-xs font-[Poppins] font-semibold uppercase tracking-wide text-[#5A7594] block mb-1.5">
              E-mailadres *
            </span>
            <input
              type="email"
              required
              data-testid="invite-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="beheer@voorbeeld.nl"
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2 focus:border-[#6BC0E8] outline-none"
              autoComplete="off"
            />
          </label>
          <label className="block">
            <span className="text-xs font-[Poppins] font-semibold uppercase tracking-wide text-[#5A7594] block mb-1.5">
              Naam (optioneel)
            </span>
            <input
              type="text"
              data-testid="invite-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Bijv. Herman de Boer"
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
            />
          </label>
        </div>
        <div className="border-t border-[#E4EEF5] px-6 py-4 flex items-center justify-end gap-3 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-[#173A5E] hover:bg-[#F4F8FB] font-[Poppins] font-semibold text-sm"
          >
            Annuleren
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="invite-submit"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#173A5E] hover:bg-[#0F2A46] text-white font-[Poppins] font-semibold text-sm disabled:opacity-60"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Uitnodiging versturen
          </button>
        </div>
      </form>
    </div>
  );
};

export const AdminUsersPage = () => {
  const { admin: current } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = "ok") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2800);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, i] = await Promise.all([fetchAdminUsers(), fetchAdminInvites()]);
      setUsers(Array.isArray(u) ? u : u.items || []);
      setInvites(i.items || []);
    } catch (e) {
      showToast(e?.response?.data?.detail || "Laden mislukt", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const doDeleteUser = async (user) => {
    try {
      await deleteAdminUser(user.id);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      showToast(`Beheerder ${user.email} verwijderd`);
    } catch (e) {
      showToast(e?.response?.data?.detail || "Verwijderen mislukt", "error");
    } finally {
      setConfirmDelete(null);
    }
  };

  const doRevoke = async (invite) => {
    try {
      await revokeAdminInvite(invite.id);
      setInvites((prev) => prev.filter((i) => i.id !== invite.id));
      showToast("Uitnodiging ingetrokken");
    } catch (e) {
      showToast(e?.response?.data?.detail || "Intrekken mislukt", "error");
    } finally {
      setConfirmRevoke(null);
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#173A5E] font-[Poppins] flex items-center gap-2">
            <Users className="w-7 h-7 text-[#2E9CD6]" />
            Gebruikers
          </h1>
          <p className="text-sm text-[#5A7594] mt-1">
            Beheer alle beheerders van Lemmer Leeft. Uitnodigingen zijn 48 uur geldig.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setInviting(true)}
          data-testid="btn-new-invite"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#173A5E] hover:bg-[#0F2A46] text-white font-[Poppins] font-semibold text-sm shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Nieuwe beheerder uitnodigen
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-[#2E9CD6]" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* Bestaande beheerders */}
          <section>
            <h2 className="text-lg font-[Poppins] font-bold text-[#173A5E] mb-3">
              Actieve beheerders ({users.length})
            </h2>
            <div className="bg-white rounded-2xl border border-[#E4EEF5] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F8FB] text-[#173A5E] font-[Poppins]">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">E-mail</th>
                    <th className="text-left px-4 py-3 font-semibold">Naam</th>
                    <th className="text-left px-4 py-3 font-semibold">Sinds</th>
                    <th className="text-right px-4 py-3 font-semibold">Acties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E4EEF5] font-[Inter]">
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.email}`} className="hover:bg-[#F9FBFD]">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Mail className="w-3.5 h-3.5 text-[#2E9CD6]" />
                          <span className="font-[Poppins] font-semibold text-[#173A5E]">{u.email}</span>
                          {current?.id === u.id && (
                            <span className="ml-1 px-1.5 py-0.5 rounded bg-[#E1F0FA] text-[#0F5C8A] text-[10px] font-semibold">
                              JIJ
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#3E5871]">{u.name || "—"}</td>
                      <td className="px-4 py-3 text-[#5A7594] text-xs whitespace-nowrap">{fmt(u.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {current?.id !== u.id && users.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setConfirmDelete(u)}
                              data-testid={`user-delete-${u.email}`}
                              className="p-2 rounded-lg text-[#8b1420] hover:bg-[#FDECEE]"
                              title="Beheerder verwijderen"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Pending invites */}
          <section>
            <h2 className="text-lg font-[Poppins] font-bold text-[#173A5E] mb-3">
              Openstaande uitnodigingen ({invites.length})
            </h2>
            {invites.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#E4EEF5] p-8 text-center text-[#5A7594] font-[Inter]">
                Er staan geen uitnodigingen open.
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-[#E4EEF5] overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F8FB] text-[#173A5E] font-[Poppins]">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">E-mail</th>
                      <th className="text-left px-4 py-3 font-semibold">Uitgenodigd door</th>
                      <th className="text-left px-4 py-3 font-semibold">Verzonden</th>
                      <th className="text-left px-4 py-3 font-semibold">Vervalt</th>
                      <th className="text-left px-4 py-3 font-semibold">Status</th>
                      <th className="text-right px-4 py-3 font-semibold">Acties</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E4EEF5] font-[Inter]">
                    {invites.map((inv) => (
                      <tr key={inv.id} data-testid={`invite-row-${inv.email}`} className="hover:bg-[#F9FBFD]">
                        <td className="px-4 py-3 font-[Poppins] font-semibold text-[#173A5E]">
                          {inv.email}
                          {inv.name && inv.name !== "Beheerder" && (
                            <span className="text-xs text-[#5A7594] font-[Inter] font-normal"> — {inv.name}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[#5A7594] text-xs">{inv.invited_by || "—"}</td>
                        <td className="px-4 py-3 text-[#5A7594] text-xs whitespace-nowrap">{fmt(inv.created_at)}</td>
                        <td className="px-4 py-3 text-[#5A7594] text-xs whitespace-nowrap">{fmt(inv.expires_at)}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${
                              inv.expired
                                ? "bg-[#FDECEE] text-[#8b1420]"
                                : "bg-[#F4E9D8] text-[#8B6914]"
                            }`}
                          >
                            <Clock className="w-3 h-3" />
                            {inv.expired ? "Verlopen" : "Wacht op acceptatie"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => setConfirmRevoke(inv)}
                              data-testid={`invite-revoke-${inv.email}`}
                              className="p-2 rounded-lg text-[#8b1420] hover:bg-[#FDECEE]"
                              title="Uitnodiging intrekken"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {inviting && (
        <InviteModal
          onClose={() => setInviting(false)}
          onSaved={() => {
            setInviting(false);
            load();
          }}
          showToast={showToast}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" data-testid="user-confirm-delete">
            <h3 className="text-xl font-bold text-[#173A5E] font-[Poppins] mb-2">Beheerder verwijderen</h3>
            <p className="text-sm text-[#5A7594] mb-4">
              Weet je zeker dat je <strong>{confirmDelete.email}</strong> als beheerder wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-lg text-[#173A5E] hover:bg-[#F4F8FB] font-[Poppins] font-semibold text-sm"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={() => doDeleteUser(confirmDelete)}
                data-testid="user-confirm-delete-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#8b1420] hover:bg-[#6a0f18] text-white font-[Poppins] font-semibold text-sm"
              >
                <Trash2 className="w-4 h-4" />
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmRevoke && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" data-testid="invite-confirm-revoke">
            <h3 className="text-xl font-bold text-[#173A5E] font-[Poppins] mb-2">Uitnodiging intrekken</h3>
            <p className="text-sm text-[#5A7594] mb-4">
              De uitnodiging voor <strong>{confirmRevoke.email}</strong> wordt ongeldig. Ze kunnen er niet meer op klikken.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmRevoke(null)}
                className="px-4 py-2 rounded-lg text-[#173A5E] hover:bg-[#F4F8FB] font-[Poppins] font-semibold text-sm"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={() => doRevoke(confirmRevoke)}
                data-testid="invite-confirm-revoke-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#8b1420] hover:bg-[#6a0f18] text-white font-[Poppins] font-semibold text-sm"
              >
                <Trash2 className="w-4 h-4" />
                Intrekken
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
};

export default AdminUsersPage;
